import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";

const Section = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= THEME ================= */
  const overlayBg = isDarkMode ? "bg-black/75" : "bg-black/60";
  const textMain = "text-white";
  const textPara = "text-gray-300";

  const statCardBg = isDarkMode
    ? "bg-gray-900/90 text-white"
    : "bg-white/90 text-[#0B1A59]";

  return (
    <section
      aria-label="AI powered SEO automation platform promoting faster search rankings"
      className="relative bg-cover bg-center bg-no-repeat bg-fixed py-24 sm:py-28 px-5 sm:px-8 md:px-16"
      style={{
        backgroundImage: "url('/Images/HOMEPIC-NEW4.png')",
      }}
    >

      {/* ================= STRUCTURED DATA ================= */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Seo Up Rank",
            applicationCategory: "SEO Tool",
            operatingSystem: "Web",
            description:
              "AI powered SEO automation platform that analyzes ranking factors, detects SEO issues, and applies automated SEO optimization.",
            url: "https://novarsis.ai/",
          }),
        }}
      />

      {/* ================= HIDDEN SEO TEXT ================= */}
      <p className="sr-only">
        Seo Up Rank is an AI powered WordPress SEO automation platform that
        helps websites improve search engine rankings, automate SEO fixes,
        analyze SEO metrics, and increase organic traffic through intelligent
        SEO technology.
      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}
      <p className="sr-only">
        AI SEO tool, automated SEO audit software, WordPress SEO automation
        platform, website ranking optimization tool, AI keyword optimization
        tool, technical SEO analysis platform, SEO analytics dashboard.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}
      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* ================= IMAGE FALLBACK ================= */}
      <img
        src="/Images/HOMEPIC-NEW4.png"
        alt="Seo Up Rank AI SEO automation platform dashboard background"
        className="hidden"
      />

      {/* ===== OVERLAY ===== */}
      <div aria-hidden="true" className={`absolute inset-0 ${overlayBg}`} />

      {/* ===== CONTENT ===== */}
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-14">

        {/* ================= LEFT ================= */}
        <article className="w-full lg:w-1/2 space-y-6 text-center lg:text-left">

          <header>

            <Reveal>
              <p className="inline-block text-[#008300] font-extrabold text-sm sm:text-base tracking-widest uppercase">
                Need Better SEO Results?
              </p>
            </Reveal>

            <Reveal>
              <h2
                className={`text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight ${textMain}`}
              >
                Smarter SEO Automation
                <br className="hidden sm:block" />
                For Faster Rankings
                <br className="hidden sm:block" />
                With AI Precision
              </h2>
            </Reveal>

            {/* Additional hidden SEO heading */}
            <h2 className="sr-only">
              AI powered SEO automation software for WordPress optimization
            </h2>

          </header>

          {/* Accent Line */}
          <div
            aria-hidden="true"
            className="w-16 h-1 bg-[#008300] rounded-full mx-auto lg:mx-0"
          />

          <Reveal>
            <p
              className={`text-base sm:text-lg text-justify leading-relaxed max-w-xl mx-auto lg:mx-0 ${textPara}`}
            >
              <strong>Seo Up Rank</strong> automates WordPress SEO by analyzing
              ranking factors, detecting optimization issues, and applying
              AI-powered SEO fixes automatically. Improve search rankings,
              increase organic traffic, and boost website visibility without
              technical complexity.
            </p>
          </Reveal>

          {/* CTA */}
          <Reveal>
            <nav className="flex justify-center lg:justify-start mt-6">
              <Link
                to="/signin"
                aria-label="Explore Seo Up Rank AI SEO automation platform"
                className="relative inline-flex items-center gap-2
                bg-[#008300] hover:bg-[#009E3C]
                text-white font-semibold py-3 px-9 rounded-md
                transition-all duration-300 shadow-lg hover:shadow-green-500/40"
              >
                Explore SEO Tool
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-md ring-1 ring-[#008300]/40"
                />
              </Link>
            </nav>
          </Reveal>

        </article>

        {/* ================= RIGHT ================= */}
        <div
          role="list"
          aria-label="Seo Up Rank SEO automation statistics"
          className="w-full lg:w-1/2 flex flex-col sm:flex-row lg:flex-col gap-6 items-center lg:items-end"
        >

          {/* CARD 1 */}
          <article
            role="listitem"
            className={`backdrop-blur-xl p-6 rounded-2xl shadow-2xl
            w-full sm:w-72 text-center ${statCardBg}`}
          >
            <Reveal>
              <figure
                className="mx-auto mb-4 w-24 h-24
                border-8 border-[#008300] rounded-full
                flex items-center justify-center text-2xl font-bold"
              >
                50+
                <figcaption className="sr-only">
                  SEO metrics analyzed automatically
                </figcaption>
              </figure>
            </Reveal>

            <Reveal>
              <p className="text-sm font-semibold">
                SEO metrics analyzed automatically
              </p>
            </Reveal>
          </article>

          {/* CARD 2 */}
          <article
            role="listitem"
            className={`backdrop-blur-xl p-6 rounded-2xl shadow-2xl
            w-full sm:w-72 text-center ${statCardBg}`}
          >
            <Reveal>
              <figure
                className="mx-auto mb-4 w-24 h-24
                border-8 border-[#008300] border-r-gray-300
                rounded-full flex items-center justify-center text-2xl font-bold"
              >
                1-Click
                <figcaption className="sr-only">
                  One click SEO fixes applied instantly
                </figcaption>
              </figure>
            </Reveal>

            <Reveal>
              <p className="text-sm font-semibold">
                SEO fixes applied instantly
              </p>
            </Reveal>
          </article>

        </div>

      </div>
    </section>
  );
};

export default Section;